/**
 * Breadcrumb trail resolver — auto-generates trails from any pathname.
 *
 * The full resolver for server and edge callers (BreadcrumbList JSON-LD, D1
 * pages, tests). The client AutoBreadcrumbs does not import this module: it
 * resolves English paths through `breadcrumbs-base.ts` and loads the Hindi
 * resolver only on /hi/ pages.
 */

import {
  HINDI_HOME_CRUMB,
  resolveTrailWith,
  titleCase,
  type BreadcrumbItem,
} from "./breadcrumbs-base";
import { resolveHindiTrail } from "./breadcrumbs-hi";

export type { BreadcrumbItem } from "./breadcrumbs-base";
export { HINDI_HOME_CRUMB } from "./breadcrumbs-base";
export { HI_PAGE_NAMES } from "./breadcrumbs-hi";

/**
 * Resolve a breadcrumb trail for a given pathname.
 * Returns null if the page should not show breadcrumbs.
 */
export function resolveTrail(pathname: string): BreadcrumbItem[] | null {
  return resolveTrailWith(pathname, resolveHindiTrail);
}

/**
 * The trail a page shows and declares: `resolveTrail(pathname)` with the last
 * crumb renamed to `leafName` when the caller knows the page's real name (an
 * edge-rendered row knows its title; the path only knows its slug). Pages with
 * no auto trail (a root, or a page that renders its own) get `[home, leaf]`.
 * The last crumb never carries an href: it is the current page.
 */
export function pageBreadcrumbTrail(pathname: string, leafName?: string): BreadcrumbItem[] {
  const trail = resolveTrail(pathname);
  if (trail && trail.length > 0) {
    return [...trail.slice(0, -1), { name: leafName ?? trail.at(-1)!.name }];
  }
  return rootedLeafTrail(pathname, leafName);
}

/** `[home, leaf]` (or just `[home]` on a root) for a path with no auto trail. */
function rootedLeafTrail(pathname: string, leafName?: string): BreadcrumbItem[] {
  const path = pathname.endsWith("/") && pathname !== "/" ? pathname.slice(0, -1) : pathname;
  const hindi = path === "/hi" || path.startsWith("/hi/");
  const home: BreadcrumbItem = hindi ? HINDI_HOME_CRUMB : { name: "Home", href: "/" };
  if (path === "/" || path === "/hi") return [home];
  return [home, { name: leafName ?? titleCase(path.split("/").at(-1)!.replace(/-/g, " ")) }];
}
